import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { productType, PublicStateEnhance } from 'utils/constant';
import { IDetailProps, ICoupon } from 'utils/types/saleTypes';
import { theme } from 'utils/style/theme';
import { inAppStoreApi } from 'apis/sales/inAppStoreApis';
import InputFlexBox from 'components/container/InputFlexBox';
import InputWrappingBox from 'components/container/InputWrappingBox';
import SubmitFlexBox from 'components/container/SubmitFlexBox';
import DefaultInput from 'components/input/DefaultInput';
import RangeDatePicker from 'components/input/RangeDatePicker';
import DefaultLayout from 'components/layout/DefaultLayout';
import DefaultSelect from 'components/select/DefaultSelect';
import InAppLocalizingForm from './InAppLocalizingForm';
import UtcTimer from 'components/etc/UtcTimer';
import TextFlexBox from 'components/container/TextFlexBox';
import SalesCurrencyTable from './SalesCurrencyTable';
import DefaultButton from 'components/button/DefaultButton';
import { useTranslation } from 'react-i18next';
import { productApis } from '../../../apis/package/productApis';
import { itemApis } from '../../../apis/package/itemApis';
import { IItem } from '../../../utils/types/storyTypes';

/**
 * * 판매 관리 - 인앱 상점 관리 - 등록 & 수정 페이지
 */

const InAppStoreInputPage = () => {
  const publicStateEnhance = PublicStateEnhance();
  const { t } = useTranslation();
  const { id, masterId } = useParams();

  const navigate = useNavigate();
  const [product, setProduct] = useState<ICoupon>();
  const [items, setItems] = useState<IItem[]>([]);

  const dateFormat = 'YYYY-MM-DD HH:mm:00';
  const today = dayjs().format(dateFormat);

  // const [detailData, setDetailData] = useState<IDetailProps[]>([
  //   { key: 0, currency: 'gem', quantity: 0, is_main: 1, first_purchase: 0 },
  // ]);
  const [detailDailyData, setDetailDailyData] = useState<IDetailProps[]>([
    { key: 0, currency: 'gem', quantity: 0 },
  ]); // 상품 타입이 "기간제일 때, 상세 데이터"

  /// input & radio event
  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    debugger;
    setProduct({ ...product!, [name]: value });
  };

  /// select event
  const handleSelectChange = (val, op) => {
    setProduct({ ...product!, [op.name]: val });
    if (val === 'daily') {
      const newData: IDetailProps[] = [];

      for (let i = 0; i <= 7; i++) {
        newData.push({
          key: i,
          currency: 'gem',
          quantity: 0,
        });
      }
      setDetailDailyData(newData);
    }
  };

  /// 날짜 조절
  const handleDateChange = (dates, dateStrings) => {
    if (!dates) {
      setProduct({
        ...product!,
        from_date: `${today}`,
        to_date: `${today}`,
      });
      return;
    }
    setProduct({
      ...product!,
      from_date: `${dateStrings[0]}`,
      to_date: `${dateStrings[1]}`,
    });
  };

  const handleSubmit = async () => {
    if (product === undefined || product === null) {
      return;
    }

    if (product.product_id === '') {
      message.error('상품 ID를 입력해주세요');
      return false;
    }

    if (product.product_name === '') {
      message.error('상품명을 입력해주세요');
      return false;
    }

    for (const lang of product.langs) {
      if (lang.title === '') {
        message.error('상품명을 입력해주세요');
        return false;
      }

      if (lang.price === null || lang.price === undefined) {
        lang.price = 0;
      }

      if (lang.banner_url === '') {
        message.error('배너 이미지를 업로드해주세요');
        return false;
      }
    }

    for (const detail of product.details) {
      if (!items.find((v) => v.id === detail.item_id)) {
        message.error('존재하지 않는 아이템입니다.');
        return false;
      }

      if (detail.quantity < 1) {
        message.error('수량을 입력해주세요');
        return false;
      }
    }

    const res = await productApis.updateProduct(product);
    if (res) {
      message.success('상품이 수정되었습니다.');
    } else {
      message.error('상품 수정에 실패했습니다.');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const fetchData = async () => {
    const res = await productApis.getProduct(Number(id), Number(masterId));
    const itemRes = await itemApis.itemList(Number(id));
    setProduct(res);
    setItems(itemRes.list);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <DefaultLayout>
      <SubmitFlexBox>
        <>
          <DefaultButton onClick={handleGoBack}>
            {t('ifYouID.header.backButton')}
          </DefaultButton>
          <DefaultButton onClick={handleSubmit}>
            {t('ifYouID.header.submitButton')}
          </DefaultButton>
        </>
      </SubmitFlexBox>

      <InputFlexBox style={{ marginTop: '2.5rem' }}>
        <>
          {product && (
            <>
              <InputWrappingBox left>
                <InAppLocalizingForm
                  product={product}
                  setProduct={setProduct as Dispatch<SetStateAction<ICoupon>>}
                />
              </InputWrappingBox>
              <InputWrappingBox>
                <DefaultInput
                  title={t('ifYouID.table.productID')}
                  name='product_id'
                  value={product.product_id}
                  onChange={handleInputChange}
                />
                <DefaultInput
                  title={t('ifYouID.table.productName')}
                  name='product_name'
                  value={product.product_name}
                  onChange={handleInputChange}
                />
                <DateSection>
                  <div>
                    <RangeDatePicker
                      title={t('ifYouID.table.salePeriod')}
                      format={dateFormat}
                      showTime={true}
                      value={[
                        dayjs(product.from_date, dateFormat),
                        dayjs(product.to_date, dateFormat),
                      ]}
                      ranges={{
                        오늘: [dayjs(), dayjs()],
                        이번주: [
                          dayjs().startOf(today as any),
                          dayjs().endOf('week'),
                        ],
                        이번달: [
                          dayjs().startOf(today as any),
                          dayjs().endOf('month'),
                        ],
                      }}
                      onChange={handleDateChange}
                      style={{ paddingBottom: '0.5rem', paddingTop: '0.5rem' }}
                    />
                    <UtcTimer
                      onGrayBox={true}
                      startDate={product.from_date ? product.from_date : today}
                      endDate={product.to_date ? product.to_date : today}
                      style={{
                        marginTop: '0.2rem',
                        marginBottom: theme.margins.bottomInModal,
                      }}
                    />
                  </div>
                </DateSection>
                <DefaultSelect
                  title={t('ifYouID.table.productType')}
                  name='product_type'
                  value={product.product_type}
                  optionList={productType}
                  onChange={handleSelectChange}
                  style={{ marginBottom: '2rem' }}
                />
                {/* 상품 종류에 따라 생기는 옵션 */}
                <TextFlexBox
                  style={{
                    display: 'block',
                    marginRight: '2rem',
                    marginBottom: theme.margins.bottomInPage,
                  }}
                >
                  <h6>{t('ifYouID.table.productTypeOption')}</h6>
                  <>
                    <SalesCurrencyTable
                      items={items}
                      product={product}
                      setProduct={
                        setProduct as Dispatch<SetStateAction<ICoupon>>
                      }
                    />
                  </>
                </TextFlexBox>
              </InputWrappingBox>
            </>
          )}
        </>
      </InputFlexBox>
    </DefaultLayout>
  );
};

export default InAppStoreInputPage;

const DateSection = styled.div`
  display: flex;
  align-items: center;

  .option_wrap {
    margin-top: -1rem;
    display: flex;
  }

  .checkbox_wrap {
    display: flex;
    align-items: center;
    margin-left: 1rem;

    > p {
      margin-left: 0.5rem;
    }
  }
`;
